import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { ArrowUp, ArrowDown, Activity, RefreshCw } from "lucide-react";

const TIMEFRAMES = [
  { key: 1,  label: "1D" },
  { key: 7,  label: "7D" },
  { key: 30, label: "30D" },
];

function fmtPrice(v) {
  if (v == null || isNaN(v)) return "-";
  if (v >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (v >= 1)    return v.toFixed(2);
  return v.toFixed(4);
}

function fmtCompact(v) {
  if (v == null || isNaN(v)) return "-";
  const abs = Math.abs(v);
  if (abs >= 1e12) return (v/1e12).toFixed(2) + "T";
  if (abs >= 1e9)  return (v/1e9).toFixed(2) + "B";
  if (abs >= 1e6)  return (v/1e6).toFixed(2) + "M";
  if (abs >= 1e3)  return (v/1e3).toFixed(2) + "K";
  return String(v);
}

export default function MarketChart({ coins }) {
  // coins: [{id, symbol, pair, price, change_24h, image, high_24h, low_24h, volume_24h, market_cap}, ...]
  const [selectedId, setSelectedId] = useState("bitcoin");
  const [days, setDays] = useState(1);
  const [chart, setChart] = useState({ points: [] });
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const abortRef = useRef(null);

  const selected = useMemo(
    () => coins?.find(c => c.id === selectedId) || coins?.[0] || null,
    [coins, selectedId]
  );

  const fetchChart = async (coinId, d) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/market/chart/${coinId}`, { params: { days: d } });
      setChart(data);
      setRefreshedAt(new Date());
    } catch {
      setChart({ points: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selected) return;
    fetchChart(selected.id, days);
    // Auto-refresh 1D charts every 60s (backend cache 60s), longer TFs every 5 min
    const ttl = days === 1 ? 60000 : 300000;
    const iv = setInterval(() => fetchChart(selected.id, days), ttl);
    const onVis = () => { if (document.visibilityState === "visible") fetchChart(selected.id, days); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [selected?.id, days]);

  const chartData = useMemo(() => {
    return (chart.points || []).map(p => ({
      t: p.t,
      p: p.p,
      label: new Date(p.t).toLocaleString([], days === 1 ? { hour: "2-digit", minute: "2-digit" } : { month: "short", day: "numeric" }),
    }));
  }, [chart, days]);

  const priceUp = (selected?.change_24h || 0) >= 0;
  const lineColor = priceUp ? "#8AE234" : "#F87171";

  return (
    <div className="nb-card p-5 overflow-hidden" data-testid="market-chart-card">
      {/* Header row: coin selector + timeframe */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            <div className="h-2 w-2 rounded-full bg-[#8AE234]"/>
            <div className="absolute inset-0 h-2 w-2 rounded-full bg-[#8AE234] animate-ping"/>
          </div>
          <div className="font-heading font-semibold text-white text-sm flex items-center gap-2">
            <Activity size={14} className="text-nb-cyan"/> Live Market
          </div>
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-[#070B14] p-1" data-testid="market-timeframe">
          {TIMEFRAMES.map(tf => (
            <button
              key={tf.key}
              data-testid={`tf-${tf.label}`}
              onClick={() => setDays(tf.key)}
              className={`px-3 py-1 rounded-md text-[11px] font-mono transition-colors ${days === tf.key ? "bg-nb-cyan/20 text-nb-cyan" : "text-slate-400 hover:text-white"}`}
            >
              {tf.label}
            </button>
          ))}
        </div>
      </div>

      {/* Coin selector chips */}
      <div className="flex overflow-x-auto no-scrollbar gap-2 pb-3 border-b border-white/5" data-testid="market-coin-selector">
        {(coins || []).map(c => {
          const active = c.id === selected?.id;
          const up = (c.change_24h || 0) >= 0;
          return (
            <button
              key={c.id}
              data-testid={`coin-${c.symbol}`}
              onClick={() => setSelectedId(c.id)}
              className={`min-w-[110px] px-3 py-2 rounded-lg border transition-colors text-left ${active ? "border-nb-cyan bg-nb-cyan/10" : "border-white/5 bg-[#070B14] hover:border-white/10"}`}
            >
              <div className="flex items-center gap-2">
                {c.image && <img src={c.image} alt={c.symbol} className="h-4 w-4 rounded-full"/>}
                <span className="text-[11px] font-mono text-slate-300">{c.pair}</span>
              </div>
              <div className="font-mono text-white text-xs mt-1">${fmtPrice(c.price)}</div>
              <div className={`text-[10px] font-mono ${up ? "text-[#8AE234]" : "text-red-400"}`}>
                {up ? "+" : ""}{(c.change_24h || 0).toFixed(2)}%
              </div>
            </button>
          );
        })}
      </div>

      {/* Live price + 24h stats */}
      {selected && (
        <div className="flex flex-wrap items-end justify-between gap-4 mt-4">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400">
              {selected.image && <img src={selected.image} alt={selected.symbol} className="h-4 w-4 rounded-full"/>}
              <span>{selected.pair}</span>
            </div>
            <div className="mt-1 font-mono font-bold text-3xl md:text-4xl text-white" data-testid="market-live-price">
              ${fmtPrice(selected.price)}
            </div>
            <div className={`mt-1 font-mono text-sm flex items-center gap-1 ${priceUp ? "text-[#8AE234]" : "text-red-400"}`} data-testid="market-change-24h">
              {priceUp ? <ArrowUp size={12}/> : <ArrowDown size={12}/>}
              {priceUp ? "+" : ""}{(selected.change_24h || 0).toFixed(2)}% <span className="text-slate-500 ml-1">24h</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 text-xs">
            <div>
              <div className="text-slate-500 uppercase tracking-widest text-[10px]">24h High</div>
              <div className="font-mono text-slate-200 mt-0.5">${fmtPrice(selected.high_24h)}</div>
            </div>
            <div>
              <div className="text-slate-500 uppercase tracking-widest text-[10px]">24h Low</div>
              <div className="font-mono text-slate-200 mt-0.5">${fmtPrice(selected.low_24h)}</div>
            </div>
            <div>
              <div className="text-slate-500 uppercase tracking-widest text-[10px]">24h Vol</div>
              <div className="font-mono text-slate-200 mt-0.5">${fmtCompact(selected.volume_24h)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="mt-4" data-testid="market-chart-area">
        {loading && chartData.length === 0 ? (
          <div className="h-[240px] flex items-center justify-center text-slate-500 text-sm">
            <RefreshCw size={14} className="animate-spin mr-2"/> Loading chart…
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={chartData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-market" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={lineColor} stopOpacity={0.35}/>
                  <stop offset="100%" stopColor={lineColor} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1f2937" strokeDasharray="3 3"/>
              <XAxis
                dataKey="label"
                tick={{ fill: "#64748B", fontSize: 10 }}
                interval={Math.max(0, Math.floor(chartData.length / 6) - 1)}
                minTickGap={20}
              />
              <YAxis
                tick={{ fill: "#64748B", fontSize: 10 }}
                domain={["auto", "auto"]}
                tickFormatter={(v) => `$${fmtPrice(v)}`}
                width={60}
              />
              <Tooltip
                contentStyle={{ background: "#0F1626", border: "1px solid #1f2937", borderRadius: 8, color: "#F8FAFC", fontSize: 12 }}
                labelFormatter={(l) => l}
                formatter={(v) => [`$${fmtPrice(v)}`, "Price"]}
              />
              <Area type="monotone" dataKey="p" stroke={lineColor} strokeWidth={2} fill="url(#grad-market)" isAnimationActive={false}/>
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500 uppercase tracking-widest">
        <span>Source · OKX Spot</span>
        {refreshedAt && <span data-testid="market-refreshed-at">Updated {refreshedAt.toLocaleTimeString()}</span>}
      </div>
    </div>
  );
}
